import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
	SubscriptionPlan, 
	SubscriptionPlanCreateDto, 
	SubscriptionPlanArea, 
	SubscriptionPlanAreaCreateDto,
	SubscriptionPlanAreaBulkCreateDto,
	SubscriptionPlanResponse,
	SubscriptionPlanAreaResponse
} from "../../modules/shared/model";
import { 
	createSubscriptionPlan as createSubscriptionPlanAction, 
	updateSubscriptionPlan as updateSubscriptionPlanAction, 
	getSubscriptionPlanById as getSubscriptionPlanByIdAction, 
	getSubscriptionPlans as getSubscriptionPlansAction, 
	deleteSubscriptionPlan as deleteSubscriptionPlanAction,
	createSubscriptionPlanArea as createSubscriptionPlanAreaAction,
	updateSubscriptionPlanArea as updateSubscriptionPlanAreaAction,
	getSubscriptionPlanAreaById as getSubscriptionPlanAreaByIdAction,
	getSubscriptionPlanAreas as getSubscriptionPlanAreasAction,
	getSubscriptionPlanAreasByPlan as getSubscriptionPlanAreasByPlanAction,
	bulkCreateSubscriptionPlanAreas as bulkCreateSubscriptionPlanAreasAction,
	bulkUpdateSubscriptionPlanAreas as bulkUpdateSubscriptionPlanAreasAction,
	deleteSubscriptionPlanArea as deleteSubscriptionPlanAreaAction,
	getSubscriptionPlansByType as getSubscriptionPlansByTypeAction
} from "../Actions/SubscriptionPlanActions";

// Subscription Plans Async Thunks
export const createSubscriptionPlan = createAsyncThunk("subscriptionPlans/create", async (data: SubscriptionPlanCreateDto, { rejectWithValue }) => {
	try { return await createSubscriptionPlanAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create subscription plan"); }
});

export const updateSubscriptionPlan = createAsyncThunk("subscriptionPlans/update", async ({ id, data }: { id: string; data: Partial<SubscriptionPlanCreateDto> }, { rejectWithValue }) => {
	try { return await updateSubscriptionPlanAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update subscription plan"); }
});

export const fetchSubscriptionPlan = createAsyncThunk("subscriptionPlans/getById", async (id: string, { rejectWithValue }) => {
	try { return await getSubscriptionPlanByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch subscription plan"); }
});

export const fetchSubscriptionPlans = createAsyncThunk("subscriptionPlans/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getSubscriptionPlansAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch subscription plans"); }
});

export const deleteSubscriptionPlan = createAsyncThunk("subscriptionPlans/delete", async (id: string, { rejectWithValue }) => {
	try { await deleteSubscriptionPlanAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete subscription plan"); }
});

export const fetchSubscriptionPlansByType = createAsyncThunk("subscriptionPlans/getByType", async (typeId: number, { rejectWithValue }) => {
	try { return await getSubscriptionPlansByTypeAction(typeId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch subscription plans by type"); }
});

// Subscription Plan Areas Async Thunks
export const createSubscriptionPlanArea = createAsyncThunk("subscriptionPlanAreas/create", async (data: SubscriptionPlanAreaCreateDto, { rejectWithValue }) => {
	try { return await createSubscriptionPlanAreaAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create subscription plan area"); }
});

export const updateSubscriptionPlanArea = createAsyncThunk("subscriptionPlanAreas/update", async ({ id, data }: { id: string; data: Partial<SubscriptionPlanAreaCreateDto> }, { rejectWithValue }) => {
	try { return await updateSubscriptionPlanAreaAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update subscription plan area"); }
});

export const fetchSubscriptionPlanArea = createAsyncThunk("subscriptionPlanAreas/getById", async (id: string, { rejectWithValue }) => {
	try { return await getSubscriptionPlanAreaByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch subscription plan area"); }
});

export const fetchSubscriptionPlanAreas = createAsyncThunk("subscriptionPlanAreas/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getSubscriptionPlanAreasAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch subscription plan areas"); }
});

export const fetchSubscriptionPlanAreasByPlan = createAsyncThunk("subscriptionPlanAreas/getByPlan", async (planId: string, { rejectWithValue }) => {
	try { return await getSubscriptionPlanAreasByPlanAction(planId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch subscription plan areas by plan"); }
});

export const bulkCreateSubscriptionPlanAreas = createAsyncThunk("subscriptionPlanAreas/bulkCreate", async (data: SubscriptionPlanAreaBulkCreateDto, { rejectWithValue }) => {
	try { return await bulkCreateSubscriptionPlanAreasAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk create subscription plan areas"); }
});

export const bulkUpdateSubscriptionPlanAreas = createAsyncThunk("subscriptionPlanAreas/bulkUpdate", async (updates: Array<{ id: string; data: Partial<SubscriptionPlanAreaCreateDto> }>, { rejectWithValue }) => {
	try { return await bulkUpdateSubscriptionPlanAreasAction(updates); } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk update subscription plan areas"); }
});

export const deleteSubscriptionPlanArea = createAsyncThunk("subscriptionPlanAreas/delete", async (id: string, { rejectWithValue }) => {
	try { await deleteSubscriptionPlanAreaAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete subscription plan area"); }
});

interface SubscriptionPlanState {
	// Subscription Plans
	plans: SubscriptionPlan[];
	currentPlan: SubscriptionPlan | null;
	plansLoading: boolean;
	plansError: string | null;
	plansTotalCount?: number;
	plansHasNextPage?: boolean;
	plansHasPreviousPage?: boolean;
	plansCurrentPage?: number;
	plansLimit?: number;
	// Subscription Plan Areas
	areas: SubscriptionPlanArea[];
	currentArea: SubscriptionPlanArea | null;
	areasLoading: boolean;
	areasError: string | null;
	areasTotalCount?: number;
}

const initialState: SubscriptionPlanState = {
	// Subscription Plans
	plans: [],
	currentPlan: null,
	plansLoading: false,
	plansError: null,
	plansTotalCount: 0,
	plansHasNextPage: false,
	plansHasPreviousPage: false,
	plansCurrentPage: 1,
	plansLimit: 10,
	// Subscription Plan Areas
	areas: [],
	currentArea: null,
	areasLoading: false,
	areasError: null,
	areasTotalCount: 0,
};

const subscriptionPlanSlice = createSlice({
	name: "subscriptionPlans",
	initialState,
	reducers: {
		clearSubscriptionPlansError(state) { state.plansError = null; },
		clearCurrentSubscriptionPlan(state) { state.currentPlan = null; },
		clearSubscriptionPlanAreasError(state) { state.areasError = null; },
		clearCurrentSubscriptionPlanArea(state) { state.currentArea = null; },
		clearSubscriptionPlanAreas(state) {
			state.areas = [];
			state.currentArea = null;
			state.areasTotalCount = 0;
		}
	},
	extraReducers: (builder) => {
		// Subscription Plans Extra Reducers
		builder
			.addCase(createSubscriptionPlan.pending, (s) => { s.plansLoading = true; s.plansError = null; })
			.addCase(createSubscriptionPlan.fulfilled, (s, a: PayloadAction<SubscriptionPlan>) => { s.plansLoading = false; s.plans.unshift(a.payload); s.plansTotalCount = (s.plansTotalCount || 0) + 1; })
			.addCase(createSubscriptionPlan.rejected, (s, a: any) => { s.plansLoading = false; s.plansError = a.payload as string; });

		builder
			.addCase(updateSubscriptionPlan.pending, (s) => { s.plansLoading = true; s.plansError = null; })
			.addCase(updateSubscriptionPlan.fulfilled, (s, a: PayloadAction<SubscriptionPlan>) => {
				s.plansLoading = false;
				const i = s.plans.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.plans[i] = a.payload;
				if (s.currentPlan?.id === a.payload.id) s.currentPlan = a.payload;
			})
			.addCase(updateSubscriptionPlan.rejected, (s, a: any) => { s.plansLoading = false; s.plansError = a.payload as string; });

		builder
			.addCase(fetchSubscriptionPlan.pending, (s) => { s.plansLoading = true; s.plansError = null; })
			.addCase(fetchSubscriptionPlan.fulfilled, (s, a: PayloadAction<SubscriptionPlan>) => { s.plansLoading = false; s.currentPlan = a.payload; })
			.addCase(fetchSubscriptionPlan.rejected, (s, a: any) => { s.plansLoading = false; s.plansError = a.payload as string; });

		builder
			.addCase(fetchSubscriptionPlans.pending, (s) => { s.plansLoading = true; s.plansError = null; })
			.addCase(fetchSubscriptionPlans.fulfilled, (s, a: PayloadAction<SubscriptionPlanResponse>) => { 
				s.plansLoading = false; 
				s.plans = a.payload.data || []; 
				s.plansTotalCount = a.payload.totalCount || 0;
				s.plansHasNextPage = a.payload.hasNextPage || false;
				s.plansHasPreviousPage = a.payload.hasPreviousPage || false;
				s.plansCurrentPage = a.payload.page || 1;
				s.plansLimit = a.payload.limit || 10;
			})
			.addCase(fetchSubscriptionPlans.rejected, (s, a: any) => { s.plansLoading = false; s.plansError = a.payload as string; });

		builder
			.addCase(deleteSubscriptionPlan.pending, (s) => { s.plansLoading = true; s.plansError = null; })
			.addCase(deleteSubscriptionPlan.fulfilled, (s, a: PayloadAction<string>) => { s.plansLoading = false; s.plans = s.plans.filter(x => x.id !== a.payload); if (s.currentPlan?.id === a.payload) s.currentPlan = null; s.plansTotalCount = Math.max(0, (s.plansTotalCount || 0) - 1); })
			.addCase(deleteSubscriptionPlan.rejected, (s, a: any) => { s.plansLoading = false; s.plansError = a.payload as string; });

		builder
			.addCase(fetchSubscriptionPlansByType.pending, (s) => { s.plansLoading = true; s.plansError = null; })
			.addCase(fetchSubscriptionPlansByType.fulfilled, (s, a: PayloadAction<SubscriptionPlan[]>) => { s.plansLoading = false; s.plans = a.payload || []; s.plansTotalCount = a.payload?.length || 0; })
			.addCase(fetchSubscriptionPlansByType.rejected, (s, a: any) => { s.plansLoading = false; s.plansError = a.payload as string; });

		// Subscription Plan Areas Extra Reducers
		builder
			.addCase(createSubscriptionPlanArea.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(createSubscriptionPlanArea.fulfilled, (s, a: PayloadAction<SubscriptionPlanArea>) => { s.areasLoading = false; s.areas.unshift(a.payload); s.areasTotalCount = (s.areasTotalCount || 0) + 1; })
			.addCase(createSubscriptionPlanArea.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(updateSubscriptionPlanArea.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(updateSubscriptionPlanArea.fulfilled, (s, a: PayloadAction<SubscriptionPlanArea>) => {
				s.areasLoading = false;
				const i = s.areas.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.areas[i] = a.payload;
				if (s.currentArea?.id === a.payload.id) s.currentArea = a.payload;
			})
			.addCase(updateSubscriptionPlanArea.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(fetchSubscriptionPlanArea.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(fetchSubscriptionPlanArea.fulfilled, (s, a: PayloadAction<SubscriptionPlanArea>) => { s.areasLoading = false; s.currentArea = a.payload; })
			.addCase(fetchSubscriptionPlanArea.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(fetchSubscriptionPlanAreas.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(fetchSubscriptionPlanAreas.fulfilled, (s, a: PayloadAction<SubscriptionPlanAreaResponse>) => { s.areasLoading = false; s.areas = a.payload.data || []; s.areasTotalCount = a.payload.totalCount || 0; })
			.addCase(fetchSubscriptionPlanAreas.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(fetchSubscriptionPlanAreasByPlan.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(fetchSubscriptionPlanAreasByPlan.fulfilled, (s, a: PayloadAction<SubscriptionPlanArea[]>) => { s.areasLoading = false; s.areas = a.payload || []; })
			.addCase(fetchSubscriptionPlanAreasByPlan.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(bulkCreateSubscriptionPlanAreas.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(bulkCreateSubscriptionPlanAreas.fulfilled, (s, a: PayloadAction<SubscriptionPlanArea[]>) => { s.areasLoading = false; s.areas.unshift(...a.payload); s.areasTotalCount = (s.areasTotalCount || 0) + a.payload.length; })
			.addCase(bulkCreateSubscriptionPlanAreas.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(bulkUpdateSubscriptionPlanAreas.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(bulkUpdateSubscriptionPlanAreas.fulfilled, (s, a: PayloadAction<SubscriptionPlanArea[]>) => { 
				s.areasLoading = false; 
				a.payload.forEach(updatedArea => {
					const index = s.areas.findIndex(area => area.id === updatedArea.id);
					if (index !== -1) {
						s.areas[index] = updatedArea;
					}
					if (s.currentArea?.id === updatedArea.id) {
						s.currentArea = updatedArea;
					}
				});
			})
			.addCase(bulkUpdateSubscriptionPlanAreas.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });

		builder
			.addCase(deleteSubscriptionPlanArea.pending, (s) => { s.areasLoading = true; s.areasError = null; })
			.addCase(deleteSubscriptionPlanArea.fulfilled, (s, a: PayloadAction<string>) => { s.areasLoading = false; s.areas = s.areas.filter(x => x.id !== a.payload); if (s.currentArea?.id === a.payload) s.currentArea = null; s.areasTotalCount = Math.max(0, (s.areasTotalCount || 0) - 1); })
			.addCase(deleteSubscriptionPlanArea.rejected, (s, a: any) => { s.areasLoading = false; s.areasError = a.payload as string; });
	}
});

export const { 
	clearSubscriptionPlansError, 
	clearCurrentSubscriptionPlan, 
	clearSubscriptionPlanAreasError,
	clearCurrentSubscriptionPlanArea,
	clearSubscriptionPlanAreas
} = subscriptionPlanSlice.actions;
export default subscriptionPlanSlice.reducer;
